import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Plus, Pencil, Trash2, X, FileText, Inbox, ExternalLink, Mail } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import api, { formatError } from "../../lib/api";

const EMPTY = { tag: "", title: "", excerpt: "", body: "", read_time: "5 min read", published: true };

function PostEditor({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial || EMPTY);
  const [saving, setSaving] = useState(false);
  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        tag: form.tag,
        title: form.title,
        excerpt: form.excerpt,
        body: form.body,
        read_time: form.read_time,
        published: form.published,
      };
      if (form.id) await api.put(`/posts/${form.id}`, payload);
      else await api.post("/posts", payload);
      toast.success(form.id ? "Post updated" : "Post created");
      onSaved();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Failed to save post");
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "w-full rounded-lg bg-obsidian border border-white/10 px-4 py-3 text-ink placeholder:text-ink-muted focus:outline-none focus:border-amber/60 focus:ring-1 focus:ring-amber/40 transition-colors";

  return (
    <div className="fixed inset-0 z-[70] overflow-y-auto bg-obsidian/90 backdrop-blur-md" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <form data-testid="post-editor" onSubmit={save} className="max-w-2xl mx-auto my-12 rounded-2xl border border-white/10 bg-surface p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-2xl font-medium">{form.id ? "Edit Post" : "New Post"}</h2>
          <button type="button" data-testid="editor-close" onClick={onClose} className="text-ink-muted hover:text-amber transition-colors">
            <X size={20} />
          </button>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-ink-secondary mb-2">Tag</label>
              <input data-testid="post-tag" required value={form.tag} onChange={update("tag")} placeholder="Venture Building" className={inputCls} />
            </div>
            <div>
              <label className="block text-sm text-ink-secondary mb-2">Read Time</label>
              <input data-testid="post-readtime" value={form.read_time} onChange={update("read_time")} placeholder="5 min read" className={inputCls} />
            </div>
          </div>
          <div>
            <label className="block text-sm text-ink-secondary mb-2">Title</label>
            <input data-testid="post-title" required value={form.title} onChange={update("title")} placeholder="Post title" className={inputCls} />
          </div>
          <div>
            <label className="block text-sm text-ink-secondary mb-2">Excerpt</label>
            <textarea data-testid="post-excerpt" required rows={2} value={form.excerpt} onChange={update("excerpt")} placeholder="Short summary shown on the card" className={inputCls} />
          </div>
          <div>
            <label className="block text-sm text-ink-secondary mb-2">Body (HTML supported: &lt;p&gt;, &lt;h3&gt;)</label>
            <textarea data-testid="post-body" required rows={8} value={form.body} onChange={update("body")} placeholder="<p>Your content...</p>" className={`${inputCls} font-mono text-sm`} />
          </div>
          <label className="flex items-center gap-3 text-sm text-ink-secondary cursor-pointer">
            <input data-testid="post-published" type="checkbox" checked={form.published} onChange={update("published")} className="h-4 w-4 accent-amber" />
            Published (visible on the site)
          </label>
        </div>
        <div className="flex justify-end gap-3 mt-8">
          <button type="button" onClick={onClose} className="rounded-lg border border-white/10 px-5 py-2.5 text-ink-secondary hover:text-ink transition-colors">
            Cancel
          </button>
          <button data-testid="post-save" type="submit" disabled={saving} className="rounded-lg bg-amber px-6 py-2.5 font-semibold text-obsidian hover:bg-amber-hover transition-colors disabled:opacity-60">
            {saving ? "Saving..." : "Save Post"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("posts");
  const [posts, setPosts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [editor, setEditor] = useState(null); // null | {} | post

  const load = useCallback(async () => {
    try {
      const [p, c] = await Promise.all([api.get("/posts?include_unpublished=true"), api.get("/contact")]);
      setPosts(Array.isArray(p.data) ? p.data : []);
      setContacts(Array.isArray(c.data) ? c.data : []);
    } catch (err) {
      toast.error("Failed to load data");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await api.delete(`/posts/${id}`);
      toast.success("Post deleted");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const removeContact = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    try {
      await api.delete(`/contact/${id}`);
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const doLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const unread = contacts.filter((c) => !c.read).length;

  return (
    <div className="min-h-screen bg-obsidian relative z-10">
      <header className="border-b border-white/[0.07] sticky top-0 bg-obsidian/80 backdrop-blur-xl z-20">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="font-display text-lg font-bold tracking-[0.06em]">
            ADMIN <span className="text-amber">PANEL</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" target="_blank" rel="noreferrer" className="hidden sm:inline-flex items-center gap-1.5 text-sm text-ink-secondary hover:text-amber transition-colors">
              View site <ExternalLink size={14} />
            </a>
            <span className="text-sm text-ink-muted hidden sm:block">{user?.email}</span>
            <button data-testid="logout-btn" onClick={doLogout} className="inline-flex items-center gap-2 rounded-lg border border-white/10 px-4 py-2 text-sm text-ink-secondary hover:text-amber hover:border-amber/40 transition-colors">
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-center gap-2 mb-8">
          <button
            data-testid="tab-posts"
            onClick={() => setTab("posts")}
            className={`inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors ${tab === "posts" ? "bg-surface2 text-ink" : "text-ink-secondary hover:text-ink"}`}
          >
            <FileText size={16} /> Blog Posts <span className="text-ink-muted">({posts.length})</span>
          </button>
          <button
            data-testid="tab-inquiries"
            onClick={() => setTab("inquiries")}
            className={`inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors ${tab === "inquiries" ? "bg-surface2 text-ink" : "text-ink-secondary hover:text-ink"}`}
          >
            <Inbox size={16} /> Inquiries
            {unread > 0 && <span className="rounded-full bg-amber text-obsidian text-xs px-2 py-0.5 font-bold">{unread}</span>}
          </button>
        </div>

        {tab === "posts" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h1 className="font-display text-3xl font-medium">Blog Posts</h1>
              <button data-testid="new-post-btn" onClick={() => setEditor(EMPTY)} className="inline-flex items-center gap-2 rounded-lg bg-amber px-5 py-2.5 font-semibold text-obsidian hover:bg-amber-hover transition-colors">
                <Plus size={16} /> New Post
              </button>
            </div>
            <div className="space-y-3">
              {posts.length === 0 && <p className="text-ink-muted">No posts yet. Create your first one.</p>}
              {posts.map((post) => (
                <div key={post.id} data-testid={`admin-post-${post.id}`} className="flex items-center gap-4 rounded-xl border border-white/[0.07] bg-surface p-5">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="text-[0.66rem] uppercase tracking-[0.14em] text-amber">{post.tag}</span>
                      {!post.published && <span className="text-[0.62rem] uppercase tracking-wide text-ink-muted border border-white/10 rounded px-1.5 py-0.5">Draft</span>}
                    </div>
                    <div className="font-display text-lg font-medium truncate">{post.title}</div>
                    <div className="text-sm text-ink-muted truncate">{post.excerpt}</div>
                  </div>
                  <button data-testid={`edit-post-${post.id}`} onClick={() => setEditor(post)} className="p-2.5 rounded-lg border border-white/10 text-ink-secondary hover:text-amber hover:border-amber/40 transition-colors">
                    <Pencil size={16} />
                  </button>
                  <button data-testid={`delete-post-${post.id}`} onClick={() => remove(post.id)} className="p-2.5 rounded-lg border border-white/10 text-ink-secondary hover:text-red-400 hover:border-red-400/40 transition-colors">
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "inquiries" && (
          <div>
            <h1 className="font-display text-3xl font-medium mb-6">Inquiries</h1>
            <div className="space-y-3">
              {contacts.length === 0 && <p className="text-ink-muted">No inquiries yet.</p>}
              {contacts.map((c) => (
                <div key={c.id} data-testid={`inquiry-${c.id}`} className={`rounded-xl border bg-surface p-5 ${c.read ? "border-white/[0.07]" : "border-amber/30"}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="font-medium text-ink">
                        {c.first_name} {c.last_name}
                        {!c.read && <span className="ml-2 inline-block h-2 w-2 rounded-full bg-amber align-middle" />}
                      </div>
                      <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1.5 text-sm text-amber hover:underline">
                        <Mail size={13} /> {c.email}
                      </a>
                      {c.service && <div className="text-sm text-ink-muted mt-0.5">Interested in: {c.service}</div>}
                    </div>
                    <button data-testid={`delete-inquiry-${c.id}`} onClick={() => removeContact(c.id)} className="p-2 rounded-lg border border-white/10 text-ink-secondary hover:text-red-400 hover:border-red-400/40 transition-colors">
                      <Trash2 size={15} />
                    </button>
                  </div>
                  <p className="text-ink-secondary mt-3 leading-relaxed">{c.message}</p>
                  <div className="text-xs text-ink-muted mt-3">{new Date(c.created_at).toLocaleString()}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {editor && <PostEditor initial={editor} onClose={() => setEditor(null)} onSaved={() => { setEditor(null); load(); }} />}
    </div>
  );
}
